
import Vue from 'vue';
import { Data as ArticleData, Picture, StreamElement } from '@root/modules/article/types/article';
import ArticleInfoCoverPaywall from '@root/modules/article/components/info/ArticleInfoCoverPaywall.vue';
import ContentImage from '@core/components/UI/ContentImage.vue';
import ContentImageLightbox from '@core/components/UI/ContentImageLightbox.vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';
import PictureInfo from '@core/components/PictureInfo.vue';
import ContentFragment from '@core/components/ContentFragment.vue';
import articleConfig from '@root/modules/article/config/article.config';
import getArticleLeadPictureCropperProps from '@root/modules/article/utils/getArticleLeadPictureCropperProps';
import getStreamElement from '@root/modules/article/utils/getStreamElement';

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface Props {
  article: ArticleData;
  size: string;
  position?: string;
}

interface Data {
  showLightbox: boolean;
}

interface Computed {
  leadElement: ArticleData['content']['leadElement']['content'][0] | null;
  picture: Picture | null;
  videoPoster: Record<string, any> | null;
  classes: Record<string, string | undefined>[];
  settings: Record<string, unknown> | null;
  authors: Array<string> | null;
  credit: string | null;
  caption: string | null;
  streamElement: StreamElement | null;
  isLive: boolean;
  isEnded: boolean;
  isLiveBlogArticle: boolean;
}

interface Methods {
  scrollToPaywall: () => void;
}

const sizeMap: Record<string, Record<string, unknown>> = articleConfig.infoCover;
const imageRatio = '16:9';

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleInfoCoverPaywall,
    ContentImage,
    ContentImageLightbox,
    ContentFragment,
    PianoCustomerStateHandler,
    PictureInfo,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    size: {
      type: String,
      required: true,
    },
    position: {
      type: String,
      default: 'center',
      required: false,
    },
  },
  data() {
    return {
      showLightbox: false,
    };
  },
  computed: {
    leadElement() {
      const { leadElement } = this.article.content;
      const leadElementContent = leadElement.content.length > 0 ? leadElement.content[0] : null;
      const isNotPicture = leadElementContent?.attrs?.type?.toLowerCase() !== 'image';
      if (isNotPicture && leadElementContent && leadElementContent.attrs) {
        leadElementContent.attrs.lead = true;
      }

      return isNotPicture ? leadElementContent : null;
    },
    picture() {
      const { leadElement } = this.article.content;
      const leadElementContent = leadElement.content.length > 0 ? leadElement.content[0] : null;

      if (!leadElementContent) {
        return null;
      }

      const baseCropperData = getArticleLeadPictureCropperProps(leadElementContent.attrs.cropperData, imageRatio, this.article.id);

      leadElementContent.attrs.cropperData = {
        ...sizeMap[this.size],
        ...leadElementContent.attrs.cropperData,
        base: baseCropperData,
      };
      return leadElementContent;
    },
    videoPoster() {
      const metaImage = this.article.metaImage;

      if (!metaImage) {
        return null;
      }

      if (!metaImage.attributes) {
        return null;
      }

      const baseCropperData = getArticleLeadPictureCropperProps(metaImage?.attributes?.cropperData, imageRatio, this.article.id);

      metaImage.attributes.cropperData = {
        ...sizeMap[this.size],
        ...metaImage?.attributes?.cropperData,
        base: baseCropperData,
      };

      return metaImage;
    },
    classes() {
      return [
        {
          [`article-info-cover--${this.position}`]: this.position,
          [`article-info-cover--${this.size}`]: this.size,
        },
      ];
    },
    settings() {
      let settings = null;

      if (this.leadElement) {
        const { embed } = this.$channelConfig('page').article.component.info;
        settings = embed || settings;
      }
      return settings;
    },
    caption() {
      return this.picture?.attrs.metadata?.title || null;
    },
    authors() {
      return this.picture?.attrs.metadata?.authors?.length && this.picture.attrs.metadata.authors[0] ? this.picture.attrs.metadata.authors : null;
    },
    credit() {
      return this.picture?.attrs.metadata?.credit || null;
    },
    streamElement() {
      return getStreamElement(this.article);
    },
    isLive() {
      if (this.streamElement) {
        return dayjs().isSameOrAfter(this.streamElement.start) && dayjs().isSameOrBefore(this.streamElement.end);
      }

      return false;
    },
    isEnded() {
      if (this.streamElement) {
        return dayjs().isSameOrAfter(this.streamElement.end);
      }

      return false;
    },
    isLiveBlogArticle() {
      return this.article.settings?.articleTemplate && this.article.settings?.articleTemplate === 'liveBlog';
    },
  },
  methods: {
    scrollToPaywall() {
      const paywall = document.querySelector('#omp-page') as HTMLElement;
      paywall.scrollIntoView({ behavior: 'smooth' });
    },
  },
});
